import { FormEvent, useEffect, useRef, useState } from "react"
import { useForm } from "@inertiajs/react"
import { ArrowLeft, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { ProgressBar, Spinner } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import type { PdfPreviewDoc } from "@/lib/pdf-preview"

const MAX_MB = 100

// Preview state per picked file. "error" is survivable: the file uploads
// with every page rendering server-side (the pre-picker behavior).
type Preview =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; doc: PdfPreviewDoc; numPages: number }

export function UploadPlanSetsDialog({
  postUrl,
  trigger,
}: {
  /** POST target — the owner's plan_sets URL (project or community). */
  postUrl: string
  trigger?: React.ReactNode
}) {
  const target = postUrl
  const [open, setOpen] = useState(false)
  const [clientError, setClientError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [previews, setPreviews] = useState<Preview[]>([])
  const [selections, setSelections] = useState<Set<number>[]>([])
  const [allPages, setAllPages] = useState<boolean[]>([])
  // Zoomed single-page view over the grid; the grid stays mounted (hidden)
  // underneath so its already-rendered thumbnails survive the round trip.
  const [zoom, setZoom] = useState<{ fileIndex: number; page: number } | null>(null)
  const zoomCache = useRef(new Map<string, string>())
  const form = useForm<{ files: File[]; page_selections: string[] }>({
    files: [],
    page_selections: [],
  })

  const destroyPreviews = (list: Preview[]) => {
    list.forEach((p) => {
      if (p.state === "ready") p.doc.destroy()
    })
  }

  const resetPicks = () => {
    destroyPreviews(previews)
    setPreviews([])
    setSelections([])
    setAllPages([])
    setZoom(null)
    zoomCache.current.clear()
  }

  const pick = (picked: FileList | null) => {
    setClientError(null)
    resetPicks()
    const files = Array.from(picked ?? [])
    const bad = files.find(
      (f) => !f.name.toLowerCase().endsWith(".pdf") || f.size > MAX_MB * 1024 * 1024,
    )
    if (bad) {
      setClientError(
        bad.size > MAX_MB * 1024 * 1024
          ? `${bad.name} is over ${MAX_MB} MB.`
          : `${bad.name} is not a PDF.`,
      )
      form.setData("files", [])
      if (inputRef.current) inputRef.current.value = ""
      return
    }
    form.setData("files", files)
    setPreviews(files.map(() => ({ state: "loading" })))
    setSelections(files.map(() => new Set()))
    setAllPages(files.map(() => false))

    files.forEach(async (file, index) => {
      try {
        // Dynamic import: pdf-preview touches browser globals at module
        // scope, so it must never load during SSR.
        const { loadPdfPreview } = await import("@/lib/pdf-preview")
        const doc = await loadPdfPreview(file)
        setPreviews((prev) =>
          prev.map((p, i) => (i === index ? { state: "ready", doc, numPages: doc.numPages } : p)),
        )
      } catch {
        // No preview — this file falls back to rendering every page.
        setPreviews((prev) => prev.map((p, i) => (i === index ? { state: "error" } : p)))
        setAllPages((prev) => prev.map((v, i) => (i === index ? true : v)))
      }
    })
  }

  const togglePage = (fileIndex: number, page: number) => {
    setAllPages((prev) => prev.map((v, i) => (i === fileIndex ? false : v)))
    setSelections((prev) =>
      prev.map((set, i) => {
        if (i !== fileIndex) return set
        const next = new Set(set)
        if (next.has(page)) next.delete(page)
        else next.add(page)
        return next
      }),
    )
  }

  const setAll = (fileIndex: number, value: boolean) => {
    setAllPages((prev) => prev.map((v, i) => (i === fileIndex ? value : v)))
    if (value) {
      setSelections((prev) => prev.map((set, i) => (i === fileIndex ? new Set<number>() : set)))
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (form.data.files.length === 0) {
      setClientError("Choose at least one PDF.")
      return
    }
    const unpicked = form.data.files.findIndex(
      (_file, i) => !allPages[i] && (selections[i]?.size ?? 0) === 0,
    )
    if (unpicked !== -1) {
      setClientError(
        `Pick at least one page of ${form.data.files[unpicked].name}, or switch it to "All pages".`,
      )
      return
    }
    form.transform((data) => ({
      ...data,
      page_selections: data.files.map((_file, i) =>
        allPages[i] ? "all" : [...(selections[i] ?? [])].sort((a, b) => a - b).join(","),
      ),
    }))
    form.post(target, {
      forceFormData: true,
      onSuccess: () => {
        form.reset()
        resetPicks()
        setOpen(false)
      },
    })
  }

  const error = clientError ?? form.errors.files
  const hasPreviews = form.data.files.length > 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          form.reset()
          resetPicks()
          setClientError(null)
          if (inputRef.current) inputRef.current.value = ""
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? <Button variant="secondary">Upload plan set</Button>}
      </DialogTrigger>
      <DialogContent
        size={hasPreviews ? "lg" : "sm"}
        onEscapeKeyDown={(event) => {
          // Esc closes the zoom, not the whole dialog with the selection.
          if (zoom) {
            event.preventDefault()
            setZoom(null)
          }
        }}
      >
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Upload plan sets</DialogTitle>
            <DialogDescription>
              Each PDF becomes a plan set. Pick the pages worth measuring —
              only those render; the rest stay a click away in the picker.
            </DialogDescription>
          </DialogHeader>
          {zoom && previews[zoom.fileIndex]?.state === "ready" && (
            <ZoomView
              doc={(previews[zoom.fileIndex] as Extract<Preview, { state: "ready" }>).doc}
              page={zoom.page}
              numPages={(previews[zoom.fileIndex] as Extract<Preview, { state: "ready" }>).numPages}
              fileName={form.data.files[zoom.fileIndex]?.name ?? ""}
              showName={form.data.files.length > 1}
              selected={
                (allPages[zoom.fileIndex] ?? false) ||
                (selections[zoom.fileIndex]?.has(zoom.page) ?? false)
              }
              cache={zoomCache.current}
              cacheKey={`${zoom.fileIndex}:${zoom.page}`}
              onToggle={() => togglePage(zoom.fileIndex, zoom.page)}
              onNavigate={(delta) =>
                setZoom((current) => current && { ...current, page: current.page + delta })
              }
              onBack={() => setZoom(null)}
            />
          )}
          <div className={cn("space-y-3 py-4", zoom && "hidden")}>
            <label htmlFor="plan-files">PDF files</label>
            <input
              ref={inputRef}
              id="plan-files"
              type="file"
              multiple
              accept="application/pdf,.pdf"
              className="block w-full cursor-pointer text-sm text-ink-body file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink-display"
              onChange={(e) => pick(e.target.files)}
            />
            {form.data.files.map((file, index) => (
              <FilePagePicker
                key={`${file.name}-${index}`}
                file={file}
                showName={form.data.files.length > 1}
                preview={previews[index] ?? { state: "loading" }}
                selection={selections[index] ?? new Set()}
                allPages={allPages[index] ?? false}
                onToggle={(page) => togglePage(index, page)}
                onZoom={(page) => setZoom({ fileIndex: index, page })}
                onAll={(value) => setAll(index, value)}
              />
            ))}
            {error && <p className="text-xs text-danger-display">{error}</p>}
            {form.processing && (
              <div className="space-y-1 pt-2">
                <ProgressBar value={form.progress?.percentage ?? 0} />
                <p className="text-xs text-ink-muted">Uploading…</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.processing}>
              Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function FilePagePicker({
  file,
  showName,
  preview,
  selection,
  allPages,
  onToggle,
  onZoom,
  onAll,
}: {
  file: File
  showName: boolean
  preview: Preview
  selection: Set<number>
  allPages: boolean
  onToggle: (page: number) => void
  onZoom: (page: number) => void
  onAll: (value: boolean) => void
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface/50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {showName && <span className="text-sm font-medium text-ink-display">{file.name}</span>}
        {preview.state === "loading" && (
          <span className="flex items-center gap-2 text-xs text-ink-muted">
            <Spinner /> Reading pages…
          </span>
        )}
        {preview.state === "error" && (
          <span className="text-xs text-ink-muted">
            Preview unavailable — every page will render after upload.
          </span>
        )}
        {preview.state === "ready" && (
          <>
            <span className="text-xs text-ink-muted">
              {preview.numPages} pages ·{" "}
              {allPages ? "all pages will render" : `${selection.size} selected`}
            </span>
            <span className="ml-auto flex gap-1">
              <Button
                type="button"
                variant={allPages ? "secondary" : "ghost"}
                size="sm"
                onClick={() => onAll(!allPages)}
              >
                All pages
              </Button>
            </span>
          </>
        )}
      </div>
      {preview.state === "ready" && (
        <div className="mt-3 grid max-h-72 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4 md:grid-cols-5">
          {Array.from({ length: preview.numPages }, (_, i) => i + 1).map((page) => (
            <PageThumb
              key={page}
              doc={preview.doc}
              page={page}
              selected={allPages || selection.has(page)}
              dimmedSelection={allPages}
              onToggle={() => onToggle(page)}
              onZoom={() => onZoom(page)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PageThumb({
  doc,
  page,
  selected,
  dimmedSelection,
  onToggle,
  onZoom,
}: {
  doc: PdfPreviewDoc
  page: number
  selected: boolean
  /** True when "All pages" is on — cards read selected but softly. */
  dimmedSelection: boolean
  onToggle: () => void
  onZoom: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [drawn, setDrawn] = useState(false)

  // Render lazily, when (and only when) the card scrolls into view — a
  // heavy CAD planset costs seconds per page even client-side, so drawing
  // all pages eagerly would freeze the grid. The observer watches the CARD,
  // not the canvas: the canvas is display:none until it has pixels, and a
  // display:none element never intersects, so observing it directly would
  // deadlock (hidden until drawn, never drawn while hidden).
  useEffect(() => {
    const canvas = canvasRef.current
    const card = buttonRef.current
    if (!canvas || !card) return
    let cancelled = false
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      void doc.renderPage(page, canvas, 220).then(() => {
        if (!cancelled) setDrawn(true)
      })
    })
    observer.observe(card)
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [doc, page])

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={onToggle}
        aria-pressed={selected}
        data-testid={`pick-page-${page}`}
        className={cn(
          "block w-full overflow-hidden rounded-md border text-left transition-colors",
          selected
            ? dimmedSelection
              ? "border-accent/40"
              : "border-accent ring-2 ring-accent/40"
            : "border-hairline hover:border-ink-muted/40",
        )}
      >
        <span className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-page">
          <canvas ref={canvasRef} className={cn("max-h-full max-w-full", !drawn && "hidden")} />
          {!drawn && <Spinner label={`Reading page ${page}`} />}
        </span>
      </button>
      <span
        className={cn(
          "pointer-events-none absolute left-1 top-1 rounded px-1.5 py-0.5 text-[11px] font-medium",
          selected ? "bg-accent text-page" : "bg-surface/90 text-ink-body",
        )}
      >
        {page}
      </span>
      <button
        type="button"
        onClick={onZoom}
        aria-label={`Zoom page ${page}`}
        data-testid={`zoom-page-${page}`}
        className="absolute bottom-1 right-1 rounded border border-hairline bg-surface/90 p-1 text-ink-muted transition-colors hover:text-ink-display"
      >
        <ZoomIn className="size-3.5" />
      </button>
    </div>
  )
}

// The zoomed single-page view: a priority render at dialog width, cached as
// a data URL so flipping back to an already-viewed page is instant (a heavy
// CAD page costs seconds per render even client-side).
function ZoomView({
  doc,
  page,
  numPages,
  fileName,
  showName,
  selected,
  cache,
  cacheKey,
  onToggle,
  onNavigate,
  onBack,
}: {
  doc: PdfPreviewDoc
  page: number
  numPages: number
  fileName: string
  showName: boolean
  selected: boolean
  cache: Map<string, string>
  cacheKey: string
  onToggle: () => void
  onNavigate: (delta: number) => void
  onBack: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [src, setSrc] = useState<string | null>(() => cache.get(cacheKey) ?? null)

  useEffect(() => {
    const cached = cache.get(cacheKey)
    if (cached) {
      setSrc(cached)
      return
    }
    setSrc(null)
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    doc
      .renderPage(page, canvas, 640, { priority: true })
      .then(() => {
        if (cancelled) return
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85)
        cache.set(cacheKey, dataUrl)
        setSrc(dataUrl)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [doc, page, cache, cacheKey])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && page > 1) onNavigate(-1)
      if (event.key === "ArrowRight" && page < numPages) onNavigate(1)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [page, numPages, onNavigate])

  return (
    <div data-testid="zoom-view" className="space-y-3 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} data-testid="zoom-back">
          <ArrowLeft className="size-4" /> All pages
        </Button>
        <span className="text-sm text-ink-muted">
          {showName && `${fileName} · `}
          Page {page} of {numPages}
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => onNavigate(-1)}
            aria-label="Previous page"
            data-testid="zoom-prev"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page >= numPages}
            onClick={() => onNavigate(1)}
            aria-label="Next page"
            data-testid="zoom-next"
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            type="button"
            variant={selected ? "secondary" : undefined}
            size="sm"
            onClick={onToggle}
            data-testid="zoom-select"
          >
            {selected ? "✓ Selected" : "Select this page"}
          </Button>
        </span>
      </div>
      <div className="max-h-[62vh] overflow-auto rounded-lg border border-hairline bg-page">
        {/* Always-mounted scratch canvas: the render effect fires while the
            PREVIOUS page's img is still on screen, so a canvas mounted only
            in the spinner branch would not exist yet when the effect reads
            its ref, and the render would silently never start. */}
        <canvas ref={canvasRef} className="hidden" />
        {src ? (
          <img src={src} alt={`Page ${page}`} className="w-full" />
        ) : (
          <span className="flex aspect-[4/3] items-center justify-center">
            <Spinner size="md" label={`Rendering page ${page}`} />
          </span>
        )}
      </div>
    </div>
  )
}
